/**
 * Campaign status transitions (section 8). Anything not in the table is rejected (CMP-01).
 */
import type { Campaign, CampaignAction, CampaignKind, CampaignStatus } from '../data/types';

type TransitionTable = Partial<Record<CampaignStatus, Partial<Record<CampaignAction, CampaignStatus>>>>;

const ONE_TIME: TransitionTable = {
  draft: { schedule: 'scheduled', send_now: 'sending' },
  scheduled: { unschedule: 'draft', cancel: 'cancelled' },
  sending: { pause: 'paused', cancel: 'cancelled' },
  paused: { resume: 'sending', cancel: 'cancelled' },
  completed: { archive: 'archived' },
  cancelled: { archive: 'archived' },
};

const AUTOMATION: TransitionTable = {
  draft: { enable: 'active' },
  active: { pause: 'paused', archive: 'archived' },
  paused: { resume: 'active', archive: 'archived' },
};

const TABLES: Record<CampaignKind, TransitionTable> = { one_time: ONE_TIME, automation: AUTOMATION };

export function allowedActions(kind: CampaignKind, status: CampaignStatus): CampaignAction[] {
  return Object.keys(TABLES[kind][status] ?? {}) as CampaignAction[];
}

/** The status the action leads to, or null when the transition is not allowed (CMP-01). */
export function nextStatus(kind: CampaignKind, status: CampaignStatus, action: CampaignAction): CampaignStatus | null {
  return TABLES[kind][status]?.[action] ?? null;
}

/** CMP-02 — only drafts are fully editable. */
export function isEditable(campaign: Pick<Campaign, 'status'>): boolean {
  return campaign.status === 'draft';
}

/** CMP-02 — what the user must do before editing, or null when editable. */
export function editBlockedReason(campaign: Pick<Campaign, 'status' | 'kind'>): string | null {
  switch (campaign.status) {
    case 'draft': return null;
    case 'scheduled': return 'Unschedule this campaign to edit it.';
    case 'active': return 'Pause this automation to edit its content, reward or trigger.';
    case 'paused':
      return campaign.kind === 'automation' ? null : 'A paused campaign cannot be edited. Resume or cancel it.';
    default: return 'This campaign can no longer be edited.';
  }
}

/** A paused automation may be edited (CMP-02); everything else must be a draft. */
export function canEditContent(campaign: Pick<Campaign, 'status' | 'kind'>): boolean {
  return editBlockedReason(campaign) === null;
}

export const ACTION_LABELS: Record<CampaignAction, string> = {
  schedule: 'Schedule',
  send_now: 'Send now',
  enable: 'Turn on',
  unschedule: 'Unschedule',
  cancel: 'Cancel campaign',
  pause: 'Pause',
  resume: 'Resume',
  archive: 'Archive',
};

export const STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  sending: 'Sending',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  cancelled: 'Cancelled',
  archived: 'Archived',
};

/** Actions that need campaign.send and a confirmation showing audience, credits and reward (CMP-05). */
export const SEND_ACTIONS: CampaignAction[] = ['schedule', 'send_now', 'enable'];

/** Whether the campaign has sent anything a report could show. */
export function hasReport(campaign: Pick<Campaign, 'kind' | 'status' | 'lastSentAt'>): boolean {
  if (campaign.kind === 'automation') return campaign.status === 'active' || campaign.status === 'paused';
  return campaign.lastSentAt !== null;
}
